import React, { useState, useEffect, useRef } from "react"
import defaultCompanyLogoUrl from "~/images/default_company_logo.svg"
import { CollapsableFilter } from "./collapsable_filter"
import { getCompanyLogoUrl, hostnameFromURL, isADomainName } from "./domain-helpers"

const t = (key: string, options?: Record<string, unknown>) => window.i18n.t(key, options)

interface SimilarCompaniesFilterProps {
  value?: string
  onChange: (value: string | undefined) => void
  isLocked?: boolean
}

interface CompanySuggestion {
  domain: string
  name: string
  email_count: number
}

// Helper to build API URL - uses session authentication automatically
function buildDomainsSuggestionUrl(query: string): string {
  return `/v2/domains-suggestion?query=${encodeURIComponent(query)}`
}

export function SimilarCompaniesFilter({
  value,
  onChange,
  isLocked = false,
}: SimilarCompaniesFilterProps) {
  const [inputValue, setInputValue] = useState(value || "")
  const [suggestions, setSuggestions] = useState<CompanySuggestion[]>([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [companyName, setCompanyName] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const suggestionsRef = useRef<HTMLDivElement>(null)

  // Sync local state when parent value changes (including when cleared)
  useEffect(() => {
    if (value) {
      setInputValue(value)
    } else {
      setInputValue("")
      setCompanyName(null)
    }
  }, [value])

  // Fetch company name when value is set (e.g., from URL params on page load)
  useEffect(() => {
    if (!value || companyName) return

    const controller = new AbortController()

    const fetchCompanyData = async () => {
      try {
        const response = await fetch(buildDomainsSuggestionUrl(value), {
          signal: controller.signal,
        })
        const data = await response.json()
        if (data.data && data.data.length > 0 && !controller.signal.aborted) {
          setCompanyName(data.data[0].name)
        }
      } catch {
        // Silently handle fetch errors
      }
    }
    fetchCompanyData()

    return () => controller.abort()
  }, [value, companyName])

  // Fetch suggestions from API
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (inputValue.length < 3) {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      const hostname = hostnameFromURL(inputValue)
      if (hostname === "") {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      // Don't fetch suggestions if input matches current selected value
      if (value && hostname === value) {
        setSuggestions([])
        setShowSuggestions(false)
        return
      }

      try {
        const response = await fetch(buildDomainsSuggestionUrl(hostname))
        const data = await response.json()
        if (data.data) {
          setSuggestions(data.data)
          setShowSuggestions(true)
        }
      } catch {
        // Silently handle fetch errors - clear suggestions
        setSuggestions([])
      }
    }

    const timeoutId = setTimeout(fetchSuggestions, 300)
    return () => clearTimeout(timeoutId)
  }, [inputValue, value])

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
  }

  const handleSelectSuggestion = (suggestion: CompanySuggestion) => {
    setInputValue("")
    setCompanyName(suggestion.name)
    onChange(suggestion.domain)
    setSuggestions([])
    setShowSuggestions(false)
    inputRef.current?.blur()
  }

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && inputValue.trim()) {
      e.preventDefault()
      const domainName = hostnameFromURL(inputValue)

      if (isADomainName(domainName)) {
        setInputValue("")
        setCompanyName(null) // Will be fetched by useEffect
        onChange(domainName)
        setSuggestions([])
        setShowSuggestions(false)
      }
    }
  }

  const handleClear = () => {
    setInputValue("")
    setCompanyName(null)
    onChange(undefined)
    setSuggestions([])
    setShowSuggestions(false)
  }

  const hasValue = !!value

  return (
    <CollapsableFilter
      label={t("js.back.discover.filters.similar_companies.title")}
      icon="magnifying-glass"
      tooltip={t("js.back.discover.filters.similar_companies.tooltip")}
      count={hasValue ? 1 : 0}
      hasValues={hasValue}
      onClear={handleClear}
      isLocked={isLocked}
      lockedTooltip={t("js.back.discover.filters.similar_companies.locked_popover")}
    >
      <label className="sr-only" htmlFor="similar-to-domain">
        {t("js.back.discover.filters.similar_companies.title")}
      </label>
      <div className="position-relative">
        <input
          ref={inputRef}
          id="similar-to-domain"
          type="text"
          className="h-input h-input--block mb-2"
          placeholder={t("js.back.discover.filters.similar_companies.placeholder")}
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions.length > 0) setShowSuggestions(true)
          }}
          autoComplete="off"
        />

        {showSuggestions && suggestions.length > 0 && (
          <div
            ref={suggestionsRef}
            className="autocomplete-suggestions position-absolute start-0 end-0"
            style={{
              top: "100%",
              zIndex: 1000,
              maxHeight: "300px",
              overflowY: "auto",
            }}
          >
            {suggestions.map((suggestion) => (
              <div
                key={suggestion.domain}
                className="autocomplete-suggestion"
                onClick={() => handleSelectSuggestion(suggestion)}
              >
                <span className="autocomplete-logo">
                  <img
                    src={getCompanyLogoUrl(suggestion.domain)}
                    alt=""
                    onError={(e) => {
                      e.currentTarget.src = defaultCompanyLogoUrl
                    }}
                  />
                </span>
                <span className="autocomplete-value">
                  {suggestion.name || suggestion.domain}
                </span>
                <span className="autocomplete-data">{suggestion.domain}</span>
                <span className="tag autocomplete-count">
                  {t("js.back.discover.filters.result", {
                    count: suggestion.email_count,
                  })}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {value && (
        <div className="company-filter-entry mb-1">
          <img
            className="company-filter-entry__logo"
            src={getCompanyLogoUrl(value)}
            alt=""
            onError={(e) => {
              e.currentTarget.src = defaultCompanyLogoUrl
            }}
          />
          <span className="company-filter-entry__name">
            {companyName || value}
          </span>
          <span className="company-filter-entry__url">{value}</span>
          <button
            type="button"
            className="h-button h-button--ghost h-button--xs h-button--icon"
            aria-label={t("js.back.discover.filters.remove")}
            onClick={handleClear}
          >
            <span className="far fa-times" aria-hidden="true" />
          </button>
        </div>
      )}
    </CollapsableFilter>
  )
}
